/* Paweli: preserve marketing, give purchase intent priority. No analytics emitted here. */
if (!customElements.get('promotion-popup')) {
  customElements.define('promotion-popup', class PromotionPopup extends HTMLElement {
    static sessionKey = 'paweli:newsletter:purchase-session:v1';
    static purchaseIntent = false; // Fail-safe if sessionStorage is unavailable on this page.

    connectedCallback() {
      this.dispose();
      this.drawer = this.closest('side-drawer');
      if (!this.drawer) return;
      this.section = this.closest('.shopify-section');
      this.events = new AbortController();
      const signal = this.events.signal;
      this.dismissKey = '_halo_promotion_popup_dismiss_today';
      this.interactionUntil = 0;
      this.navigating = false;
      this.dismissed = false;
      const capture = { capture: true, signal };
      document.addEventListener('click', e => this.onIntent(e), capture);
      document.addEventListener('submit', e => this.onIntent(e), capture);
      for (const name of ['pointerdown', 'keydown', 'change', 'input']) {
        document.addEventListener(name, e => {
          if (e.target instanceof Element && e.target.closest('variant-selects, .product-form__input, product-form-component, .sticky-cart')) {
            this.interactionUntil = Date.now() + 2000;
          }
        }, capture);
      }
      this.drawer.addEventListener('click', e => {
        if (e.target.closest('.popup__close, [id^="Drawer-Overlay-"]')) {
          e.preventDefault();
          e.stopImmediatePropagation(); // Do not run generic SideDrawer close a second time.
          this.dismiss();
        }
      }, capture);
      this.drawer.addEventListener('keyup', e => {
        if (e.key === 'Escape' && this.isOpen()) {
          e.stopImmediatePropagation();
          this.dismiss();
        }
      }, capture);
      window.addEventListener('pagehide', () => this.clearTimers(), { signal });
      window.addEventListener('pageshow', event => {
        if (!event.persisted) return;
        this.navigating = false;
        if (!this.isOpen()) this.schedule(1000);
      }, { signal });
      document.addEventListener('shopify:section:unload', e => {
        if (e.target === this.section || e.target.contains?.(this)) this.dispose();
      }, { signal });
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:select', e => {
          if (e.target === this.section) this.schedule(0);
        }, { signal });
        document.addEventListener('shopify:section:deselect', e => {
          if (e.target === this.section) { this.clearTimers(); this.hide(); }
        }, { signal });
      }
      // Successful AJAX add from existing theme pubsub, also covers non-form integrations.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, data => {
          if (data?.source === 'product-form-component' && data.cartData && !data.cartData.status) this.suppress();
        });
      }
      this.cartObserver = new MutationObserver(() => {
        if (this.cartOpen() && this.isOpen()) this.hide(false);
      });
      document.querySelectorAll('cart-drawer').forEach(cart => this.cartObserver.observe(cart, { attributes: true, attributeFilter: ['class'] }));
      if (!window.Shopify?.designMode) this.schedule(Number(this.dataset.delay) || 0);
    }

    disconnectedCallback() { this.dispose(); }

    clearTimers() {
      clearTimeout(this.openTimer);
      clearTimeout(this.focusTimer);
      this.openTimer = this.focusTimer = null;
    }

    dispose() {
      this.clearTimers();
      this.events?.abort();
      this.unsubscribe?.();
      this.unsubscribe = null;
      this.cartObserver?.disconnect();
      this.cartObserver = null;
      if (this.drawer) this.hide(false);
    }

    isOpen() { return this.drawer?.classList.contains('active'); }
    cartOpen() { return !!document.querySelector('cart-drawer.active, cart-drawer.open'); }
    blockedPath() { return /\/(cart|checkout|checkouts)(\/|$)/i.test(location.pathname); }

    sessionSuppressed() {
      if (this.constructor.purchaseIntent) return true;
      try { return sessionStorage.getItem(this.constructor.sessionKey) === '1'; }
      catch (_) { return false; }
    }

    manuallyDismissed() {
      if (this.dismissed) return true;
      for (const name of ['localStorage', 'sessionStorage']) {
        try {
          const record = JSON.parse(window[name].getItem(this.dismissKey) || 'null');
          if (record?.exp > Date.now()) return true;
        } catch (_) { /* Storage denied: retain in-memory dismissal. */ }
      }
      return false;
    }

    suppress() {
      this.constructor.purchaseIntent = true;
      try { sessionStorage.setItem(this.constructor.sessionKey, '1'); } catch (_) {}
      this.clearTimers();
      this.hide(false);
    }

    onIntent(event) {
      const el = event.target instanceof Element ? event.target : null;
      if (!el) return;
      const form = event.type === 'submit' ? el : el.closest('form');
      const button = el.closest('button, input[type="submit"]');
      const enabled = button && !button.disabled && button.getAttribute('aria-disabled') !== 'true';
      if (form && /\/cart\/add(?:\.js)?(?:\?|$)/.test(form.getAttribute('action') || '') &&
          (event.type === 'submit' || (enabled && (button.type === 'submit' || button.classList.contains('add-to-cart-button'))))) {
        this.suppress(); // At accepted intent, before async response/timer race, including failed attempts.
        return;
      }
      const link = el.closest('a[href]');
      const url = link ? new URL(link.href, location.href) : null;
      if ((url && /\/(cart|checkout|checkouts)(\/|$)/i.test(url.pathname)) ||
          el.closest('[name="checkout"], .shopify-payment-button, shopify-accelerated-checkout')) {
        this.navigating = true;
        this.clearTimers();
        this.hide(false);
      } else if (el.closest('.cart-icon--drawer, [data-side-drawer*="Cart"]')) {
        this.interactionUntil = Date.now() + 2000;
        this.hide(false);
      }
    }

    unsafeNow() {
      const otherDrawer = [...document.querySelectorAll('side-drawer.active, side-drawer.open, dialog[open]')].some(e => e !== this.drawer);
      const focus = document.activeElement;
      const editing = focus && !this.contains(focus) && focus.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="submit"]), textarea, select, [contenteditable="true"]');
      return this.cartOpen() || otherDrawer || document.hidden || Date.now() < this.interactionUntil || editing ||
        !!document.querySelector('product-form-component .add-to-cart-button.loading, product-info [aria-busy="true"]');
    }

    schedule(delay) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
      if (!this.isConnected || !this.events || this.events.signal.aborted || this.sessionSuppressed() || this.manuallyDismissed() || this.blockedPath() || this.navigating) return;
      this.openTimer = setTimeout(() => {
        this.openTimer = null;
        if (!this.isConnected || this.sessionSuppressed() || this.manuallyDismissed() || this.blockedPath() || this.navigating) return;
        if (this.unsafeNow()) { this.schedule(1000); return; }
        this.show();
      }, delay);
    }

    show() {
      if (this.isOpen()) return;
      this.activeElement = document.activeElement;
      this.drawer.classList.add('active');
      document.body.classList.add('overflow-hidden');
      document.documentElement.setAttribute('scroll-lock', '');
      this.focusTimer = setTimeout(() => {
        this.focusTimer = null;
        if (!this.isOpen() || !this.isConnected || this.sessionSuppressed() || this.cartOpen()) return;
        trapFocus(this, this.querySelector('.field__input, .popup__close'));
      }, 350);
    }

    hide(restoreFocus = true) {
      clearTimeout(this.focusTimer);
      this.focusTimer = null;
      if (!this.isOpen()) return;
      this.drawer.classList.remove('active');
      // Never release another drawer's lock or focus trap.
      if (!this.cartOpen() && ![...document.querySelectorAll('side-drawer.active, side-drawer.open, dialog[open]')].some(e => e !== this.drawer)) {
        document.body.classList.remove('overflow-hidden');
        document.documentElement.removeAttribute('scroll-lock');
        removeTrapFocus(restoreFocus && this.activeElement?.isConnected ? this.activeElement : undefined);
      }
    }

    dismiss() {
      this.dismissed = true;
      this.clearTimers();
      const now = new Date();
      const exp = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
      for (const name of ['localStorage', 'sessionStorage']) {
        try { window[name].setItem(this.dismissKey, JSON.stringify({ v: '1', exp })); break; } catch (_) {}
      }
      this.hide();
    }
  });
}
